import React, { useState } from 'react';
import { AccordionDetails, Box } from '@material-ui/core';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useTranslation } from 'react-i18next';
import { CanceledPartially, LinesAdded, PaidPartially, isPaidPartiallyGuard } from '../../../../../types';
import DetailsRow from '../DetailsRow';
import { DetailTitle, DetailsRowContainer, ExpansionPanel, ExpansionPanelSummary, Link, SpacedChip } from '../style';
import { stateToTextMap } from '../utils';
import { usePriceService } from '../../../../../utils/hooks/priceService';
import PaidChip from '../PaidChip';
import { routePaths } from '../../../../../routePaths';
import { OrderEvent } from '../../../../../server-types';
import { useUser } from '../../../../../utils/hooks/auth';
import { useDateTimeService } from '../../../../../utils/hooks/dateTimeService';
import { Typography } from '../../../../../ui-kit';

const OrderLineDetails: React.FC<PaidPartially | LinesAdded | CanceledPartially> = props => {
    const { timestamp, details, eventType } = props;
    const { priceService } = usePriceService();
    const { dateTimeService } = useDateTimeService();
    const { user } = useUser();
    const [expanded, setExpanded] = useState<boolean>(false);
    const { t } = useTranslation();

    const { orderLines } = details;
    let productCount = 0;
    let amount = 0;
    orderLines.forEach(line => {
        productCount = priceService.sumQuantity(line.quantity, productCount);
        amount = priceService.add(amount, line.price);
    });

    const statusNode = eventType === OrderEvent.PaidPartially
        ? (
            <>
                <Box mr={.5} display='flex'>
                    <MonetizationOnIcon fontSize='small' />
                </Box>
                {t(...stateToTextMap[eventType])}
            </>
        )
        : t(...stateToTextMap[eventType]);

    const finalAmount = priceService.toLocaleString(amount);
    const changeTime = dateTimeService.localizeShortDateTime(timestamp, user.cultureCode);
    const handleChange = () => setExpanded(!expanded);
    return (
        <ExpansionPanel expanded={expanded} onChange={handleChange}>
            <ExpansionPanelSummary
                reason={eventType}
                expandIcon={<ExpandMoreIcon />}
            >
                <Box display='flex' alignItems='center' justifyContent="space-between" width="100%">
                    <Box>
                        <Typography variant='h6' color='inherit'>
                            <DetailTitle>
                                {statusNode}
                            </DetailTitle>
                        </Typography>
                        <span>{changeTime}</span>
                    </Box>
                    <Box display='flex' flexWrap='wrap' justifyContent='center' ml={2}>
                        <SpacedChip changeReason={eventType} label={t('{{count}} item', { count: productCount })} />
                        {isPaidPartiallyGuard(props) ? (
                            <PaidChip
                                changeReason={eventType}
                                amount={finalAmount}
                            />
                        ) : (
                            <SpacedChip
                                changeReason={eventType}
                                label={eventType === OrderEvent.CanceledPartially ? `- ${finalAmount}` : finalAmount}
                            />
                        )}
                    </Box>
                </Box>
            </ExpansionPanelSummary>
            <AccordionDetails>
                <Box display='flex' flexGrow={1} flexDirection='column'>
                    {isPaidPartiallyGuard(props) ?
                        (
                            <DetailsRowContainer mt={4}>
                                <Typography variant='h6'>
                                    {t('Bill:')}
                                    <Link ml={1.5} to={`${routePaths.bills}/${props.details.bill.id}`}>
                                        {props.details.bill.billNumber}
                                    </Link>
                                </Typography>
                            </DetailsRowContainer>
                        )
                    : null}
                    {orderLines.map(line => (
                        <DetailsRow key={line.product.id} {...line} />
                    ))}
                </Box>
            </AccordionDetails>
        </ExpansionPanel>
    );
};

export default OrderLineDetails;
